if (!window.MpsSearchRelevance) {
  window.MpsSearchRelevance = (() => {
    const STOP_WORDS = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'on', 'the', 'to', 'with']);
    const listSignatures = new WeakMap();
    let sortScheduled = false;

    const INTENT_GROUPS = [
      {
        words: ['exhaust', 'exhausts', 'silencer', 'silencers', 'muffler', 'mufflers', 'decat', 'collector'],
        aliases: ['exhaust', 'silencer', 'silencers', 'muffler', 'mufflers', 'decat', 'collector', 'full system', 'slip on', 'slipon']
      },
      {
        words: ['filter', 'filters'],
        aliases: ['filter', 'filters', 'air filter']
      },
      {
        words: ['screen', 'screens', 'windshield', 'windscreen', 'visor'],
        aliases: ['screen', 'screens', 'windshield', 'windscreen', 'visor']
      },
      {
        words: ['lever', 'levers'],
        aliases: ['lever', 'levers', 'clutch', 'brake']
      },
      {
        words: ['mirror', 'mirrors'],
        aliases: ['mirror', 'mirrors']
      },
      {
        words: ['tail', 'tidy', 'tailtidy'],
        aliases: ['tail tidy', 'tailtidy']
      },
      {
        words: ['frame', 'slider', 'sliders'],
        aliases: ['frame slider', 'frame sliders', 'crash protector']
      },
      {
        words: ['radiator', 'guard'],
        aliases: ['radiator guard', 'radiator grille']
      },
      {
        words: ['hugger', 'fender', 'fenders'],
        aliases: ['hugger', 'fender', 'fenders']
      },
      {
        words: ['tank', 'pad', 'pads'],
        aliases: ['tank pad', 'tank pads', 'protector']
      }
    ];

    const VEHICLE_INTENT_WORDS = new Set(
      INTENT_GROUPS.reduce((words, group) => words.concat(group.words), [])
    );

    const GENERIC_COMPATIBILITY_TERMS = [
      'bike range',
      'model range',
      'motorcycle range',
      'naked bike range',
      'naked bike',
      'range 20',
      'universal',
      'generic'
    ];

    const ALLOWED_MODEL_QUALIFIERS = new Set([
      'abs',
      'adv',
      'adventure',
      'edition',
      'factory',
      'gt',
      'hp',
      'limited',
      'plus',
      'pro',
      'r',
      'rally',
      'rr',
      'rs',
      's',
      'se',
      'sp',
      'sport',
      'standard',
      'touring',
      'x',
      'xc'
    ]);

    function normalise(value) {
      return (value || '')
        .toString()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/\+/g, ' ')
        .replace(/['\u2019]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function stripSearchSyntax(value) {
      return (value || '')
        .toString()
        .replace(/\s+AND\s+/gi, ' ')
        .replace(/\s+OR\s+/gi, ' ')
        .replace(/\bproduct_type:[^\s]+/gi, ' ')
        .trim();
    }

    function tokenise(value) {
      return normalise(stripSearchSyntax(value))
        .split(' ')
        .filter((token) => token && !STOP_WORDS.has(token));
    }

    function makeField(value) {
      const normal = normalise(value);
      return {
        normal,
        compact: normal.replace(/\s+/g, '')
      };
    }

    function includesToken(field, token) {
      if (!field.normal || !token) return false;
      if (field.normal.split(' ').includes(token)) return true;
      if (token.length > 1 && field.normal.includes(token)) return true;
      return field.compact.includes(token);
    }

    function includesPhrase(field, queryNormal, queryCompact) {
      if (!field.normal || !queryNormal) return false;
      return field.normal.includes(queryNormal) || (queryCompact.length >= 4 && field.compact.includes(queryCompact));
    }

    function allTokensIn(field, tokens) {
      return tokens.length > 0 && tokens.every((token) => includesToken(field, token));
    }

    function countTokenMatches(field, tokens) {
      return tokens.reduce((count, token) => count + (includesToken(field, token) ? 1 : 0), 0);
    }

    function getFieldTokens(field) {
      return field.normal.split(' ').filter(Boolean);
    }

    function getSequenceIndex(tokens, sequence) {
      if (sequence.length === 0 || tokens.length < sequence.length) return -1;

      for (let index = 0; index <= tokens.length - sequence.length; index += 1) {
        if (sequence.every((token, offset) => tokens[index + offset] === token)) return index;
      }

      return -1;
    }

    function hasDisallowedNextModelToken(tokens, sequenceIndex, sequenceLength) {
      if (sequenceIndex === -1) return false;

      const nextToken = tokens[sequenceIndex + sequenceLength];
      if (!nextToken || /^\d/.test(nextToken)) return false;
      if (VEHICLE_INTENT_WORDS.has(nextToken) || ALLOWED_MODEL_QUALIFIERS.has(nextToken)) return false;

      return true;
    }

    function hasExtraModelQualifier(field, vehicle) {
      const tokens = getFieldTokens(field);
      const fullSequence = [vehicle.make].concat(vehicle.modelTokens);
      const fullSequenceIndex = getSequenceIndex(tokens, fullSequence);
      const modelSequenceIndex = getSequenceIndex(tokens, vehicle.modelTokens);

      if (hasDisallowedNextModelToken(tokens, fullSequenceIndex, fullSequence.length)) return true;
      if (hasDisallowedNextModelToken(tokens, modelSequenceIndex, vehicle.modelTokens.length)) return true;

      if (vehicle.modelCompact.length >= 4) {
        const compactModelIndex = tokens.findIndex((token) => token === vehicle.modelCompact);
        if (hasDisallowedNextModelToken(tokens, compactModelIndex, 1)) return true;
      }

      return false;
    }

    function getVehicleQuery(tokens) {
      const vehicleTokens = tokens.filter((token) => !VEHICLE_INTENT_WORDS.has(token));
      const hasModelSignal = vehicleTokens.some((token) => /\d/.test(token));

      if (vehicleTokens.length < 2 || !hasModelSignal) return null;

      const make = vehicleTokens[0];
      const modelTokens = vehicleTokens.slice(1);
      const modelNormal = modelTokens.join(' ');
      const fullNormal = [make, modelNormal].join(' ');

      return {
        make,
        modelTokens,
        modelNormal,
        modelCompact: modelNormal.replace(/\s+/g, ''),
        fullNormal,
        fullCompact: fullNormal.replace(/\s+/g, '')
      };
    }

    function hasGenericCompatibilityTitle(fields) {
      return (
        fields.title.normal.split(' ').includes('range')
        || GENERIC_COMPATIBILITY_TERMS.some((term) => fields.title.normal.includes(term))
      );
    }

    function makeTagCount(fields, vehicle) {
      if (!fields.tags.normal || !vehicle?.make) return 0;
      return fields.tags.normal.split(' ').filter((token) => token === vehicle.make).length;
    }

    function scoreVehicleItem(fields, vehicle, tokens) {
      const titleFull = includesPhrase(fields.title, vehicle.fullNormal, vehicle.fullCompact);
      const handleFull = includesPhrase(fields.handle, vehicle.fullNormal, vehicle.fullCompact);
      const tagsFull = includesPhrase(fields.tags, vehicle.fullNormal, vehicle.fullCompact);

      const titleModel = includesPhrase(fields.title, vehicle.modelNormal, vehicle.modelCompact);
      const handleModel = includesPhrase(fields.handle, vehicle.modelNormal, vehicle.modelCompact);
      const tagsModel = includesPhrase(fields.tags, vehicle.modelNormal, vehicle.modelCompact);
      const allModel = includesPhrase(fields.all, vehicle.modelNormal, vehicle.modelCompact);

      const titleMake = includesToken(fields.title, vehicle.make);
      const handleMake = includesToken(fields.handle, vehicle.make);
      const tagsMake = includesToken(fields.tags, vehicle.make);
      const allMake = includesToken(fields.all, vehicle.make);

      const exactTitle = titleFull || (titleMake && titleModel);
      const exactHandle = handleFull || (handleMake && handleModel);
      const exactTags = tagsFull || (tagsMake && tagsModel);
      const exactModelAnywhere = titleModel || handleModel || tagsModel || allModel;
      const makeAnywhere = titleMake || handleMake || tagsMake || allMake;
      const partialModelMatches = countTokenMatches(fields.all, vehicle.modelTokens);
      const missingModelTokens = Math.max(0, vehicle.modelTokens.length - partialModelMatches);

      let score = 0;

      if (exactTitle) score += 7200;
      else if (titleModel) score += 5400;

      if (exactHandle) score += 6600;
      else if (handleModel) score += 4800;

      if (exactTags) score += 3300;
      else if (tagsModel && tagsMake) score += 2900;
      else if (tagsModel) score += 1800;

      if (titleFull) score += 1200;
      if (handleFull) score += 1000;
      if (tagsFull) score += 700;
      if (allMake && allModel) score += 650;

      tokens.forEach((token) => {
        const weight = tokenWeight(token);
        if (includesToken(fields.title, token)) score += weight * 0.9;
        if (includesToken(fields.handle, token)) score += weight * 0.8;
        if (includesToken(fields.tags, token)) score += weight * 0.45;
      });

      if (makeAnywhere && exactModelAnywhere) score += 650;
      if (!makeAnywhere && (titleModel || handleModel)) score += 450;

      if (!exactModelAnywhere) {
        if (makeAnywhere) score -= 2400;
        if (partialModelMatches > 0) score -= 900 + (missingModelTokens * 550);
      } else if (!exactTitle && !exactHandle && exactTags) {
        score -= 450;
      }

      if (makeAnywhere && !exactModelAnywhere && partialModelMatches === 0) score -= 700;

      if (hasGenericCompatibilityTitle(fields) && !exactTitle && !exactHandle) {
        score -= exactTags ? 3500 : 1500;
      }

      if (exactTags && !exactTitle && !exactHandle) {
        score -= Math.min(1400, Math.max(0, makeTagCount(fields, vehicle) - 3) * 100);
      }

      if (
        (exactTitle && hasExtraModelQualifier(fields.title, vehicle))
        || (exactHandle && hasExtraModelQualifier(fields.handle, vehicle))
      ) {
        score -= 19000;
      }

      return score;
    }

    function tokenWeight(token) {
      if (/^\d+$/.test(token)) return 90;
      if (/\d/.test(token)) return 80;
      if (token.length === 1) return 18;
      if (token.length === 2) return 45;
      return 35;
    }

    function getItemFields(item) {
      const titleEl = item.querySelector('.card__title, .predictive-result__title');
      const title = item.dataset.searchTitle || titleEl?.textContent || '';
      const vendor = item.dataset.searchVendor || item.dataset.vendor || '';
      const type = item.dataset.searchType || item.dataset.productType || '';
      const tags = item.dataset.searchTags || '';
      const handle = item.dataset.searchHandle || '';
      const sku = item.dataset.searchSku || '';
      const text = item.dataset.searchText || item.textContent || '';

      return {
        title: makeField(title),
        vendor: makeField(vendor),
        type: makeField(type),
        tags: makeField(tags),
        handle: makeField(handle),
        sku: makeField(sku),
        all: makeField([title, vendor, type, tags, handle, sku, text].join(' '))
      };
    }

    function intentScore(queryTokens, fields) {
      let score = 0;
      const haystack = `${fields.title.normal} ${fields.type.normal}`;

      INTENT_GROUPS.forEach((group) => {
        const requested = queryTokens.some((token) => group.words.includes(token));
        if (!requested) return;

        const matched = group.aliases.some((alias) => haystack.includes(alias));
        score += matched ? 360 : -120;
      });

      return score;
    }

    function scoreItem(item, query, originalIndex) {
      const queryNormal = normalise(stripSearchSyntax(query));
      const queryCompact = queryNormal.replace(/\s+/g, '');
      const tokens = tokenise(query);
      const fields = getItemFields(item);
      const vehicle = getVehicleQuery(tokens);

      if (queryNormal.length < 3 || tokens.length < 1) return 0;

      let score = 0;

      if (vehicle) {
        score = scoreVehicleItem(fields, vehicle, tokens);
        score += intentScore(tokens, fields);
        return score + Math.max(0, 100 - originalIndex) / 1000;
      }

      if (includesPhrase(fields.title, queryNormal, queryCompact)) score += 1600;
      if (includesPhrase(fields.handle, queryNormal, queryCompact)) score += 1500;
      if (includesPhrase(fields.vendor, queryNormal, queryCompact)) score += 1500;
      if (includesPhrase(fields.type, queryNormal, queryCompact)) score += 1350;
      if (includesPhrase(fields.tags, queryNormal, queryCompact)) score += 1300;
      if (includesPhrase(fields.all, queryNormal, queryCompact)) score += 700;

      if (allTokensIn(fields.title, tokens)) score += 700;
      if (allTokensIn(fields.handle, tokens)) score += 650;
      if (allTokensIn(fields.vendor, tokens)) score += 640;
      if (allTokensIn(fields.type, tokens)) score += 600;
      if (allTokensIn(fields.tags, tokens)) score += 560;
      if (allTokensIn(fields.all, tokens)) score += 360;

      let missingTokens = 0;
      tokens.forEach((token) => {
        const weight = tokenWeight(token);
        const inTitle = includesToken(fields.title, token);
        const inHandle = includesToken(fields.handle, token);
        const inTags = includesToken(fields.tags, token);
        const inVendor = includesToken(fields.vendor, token);
        const inType = includesToken(fields.type, token);
        const inAll = includesToken(fields.all, token);

        if (inTitle) score += weight * 2;
        if (inHandle) score += weight * 1.8;
        if (inTags) score += weight * 1.5;
        if (inVendor && !/\d/.test(token)) score += weight * 1.1;
        if (inType) score += weight * 0.8;
        if (inAll) score += weight * 0.25;

        if (!inAll) {
          missingTokens += 1;
          score -= /\d/.test(token) ? 340 : 90;
        }
      });

      if (tokens.length >= 3 && missingTokens > 0) score -= missingTokens * 120;

      score += intentScore(tokens, fields);

      return score + Math.max(0, 100 - originalIndex) / 1000;
    }

    function shouldHideLowConfidenceMatches(list, tokens, vehicle, scored) {
      if (list.dataset.searchRelevanceContext !== 'search-page') return false;
      if (scored.length < 8) return false;
      if (vehicle && scored.some(({ score }) => score >= 6000)) return true;

      const topScore = scored[0]?.score || 0;
      if (topScore < 1100) return false;

      return tokens.length <= 3;
    }

    function getMinimumVisibleScore(tokens, vehicle) {
      if (vehicle) return 3000;
      if (tokens.length === 1) return 220;
      return 420;
    }

    function getListQuery(list) {
      const explicitQuery = list.dataset.searchRelevanceQuery;
      if (explicitQuery) return explicitQuery;

      const predictiveSearch = list.closest('predictive-search');
      const predictiveInput = predictiveSearch?.querySelector('.js-search-input');
      if (predictiveInput?.value) return predictiveInput.value;

      return new URLSearchParams(window.location.search).get('q') || '';
    }

    function sortList(list) {
      // Respect a sort the shopper picked (e.g. price low to high) on the search page.
      const chosenSort = new URLSearchParams(window.location.search).get('sort_by');
      if (list.dataset.searchRelevanceContext === 'search-page' && chosenSort && chosenSort !== 'relevance') return;

      const query = getListQuery(list);
      const tokens = tokenise(query);
      const vehicle = getVehicleQuery(tokens);
      if (tokens.length < 2) return;

      const items = Array.from(list.children).filter((item) => item.matches('[data-search-relevance-item]'));
      if (items.length < 2) return;

      items.forEach((item, index) => {
        if (!item.dataset.searchOriginalIndex) item.dataset.searchOriginalIndex = index.toString();
      });

      const signature = `${query}::${items.map((item) => item.dataset.searchKey || item.dataset.searchHandle || item.textContent.trim()).join('|')}`;
      if (listSignatures.get(list) === signature) return;
      listSignatures.set(list, signature);

      const scored = items
        .map((item, index) => ({
          item,
          score: scoreItem(item, query, Number(item.dataset.searchOriginalIndex || index))
        }))
        .sort((a, b) => (b.score - a.score) || (Number(a.item.dataset.searchOriginalIndex) - Number(b.item.dataset.searchOriginalIndex)));

      if (scored[0].score <= 0) return;

      const limit = Number.parseInt(list.dataset.searchRelevanceLimit, 10) || 0;
      const hideLowConfidenceMatches = shouldHideLowConfidenceMatches(list, tokens, vehicle, scored);
      const minimumVisibleScore = getMinimumVisibleScore(tokens, vehicle);
      const anchor = Array.from(list.children).find((item) => !item.matches('[data-search-relevance-item]')) || null;

      const keepOrder = list.hasAttribute('data-search-keep-order');
      scored.forEach(({ item, score }, index) => {
        // In brand order, the limit counts from the top of the list as shown.
        const position = keepOrder ? Number(item.dataset.searchOriginalIndex) : index;
        if (limit > 0 || hideLowConfidenceMatches) {
          item.hidden = (limit > 0 && position >= limit) || (hideLowConfidenceMatches && score < minimumVisibleScore);
        } else {
          item.hidden = false;
        }
        // Search page in Manufacturer order: keep the brand order from the server, only hide weak matches.
        if (!list.hasAttribute('data-search-keep-order')) list.insertBefore(item, anchor);
      });
    }

    function sortAll(root = document) {
      root.querySelectorAll('[data-search-relevance-list]').forEach(sortList);
    }

    function scheduleSort() {
      if (sortScheduled) return;
      sortScheduled = true;
      window.requestAnimationFrame(() => {
        sortScheduled = false;
        sortAll();
      });
    }

    function init() {
      sortAll();

      document.addEventListener('on:facet-filters:updated', scheduleSort);
      document.addEventListener('shopify:section:load', (event) => sortAll(event.target));

      new MutationObserver((mutations) => {
        const hasSearchUpdate = mutations.some((mutation) => {
          if (mutation.target.matches?.('[data-search-relevance-list]')) return true;

          return Array.from(mutation.addedNodes).some((node) => (
            node.nodeType === Node.ELEMENT_NODE
            && (
              node.matches?.('[data-search-relevance-list]')
              || node.querySelector?.('[data-search-relevance-list]')
            )
          ));
        });

        if (hasSearchUpdate) scheduleSort();
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    return {
      sortAll,
      scoreItem,
      normalise,
      tokenise
    };
  })();
}
